import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ListChecks } from 'lucide-react'
import type { BesprechungOffenerPunkt } from '@symphenia/shared-types'
import { api } from '@/lib/axios'
import { fmtDate } from '@/lib/format-date'
import { formCard } from '@/lib/form-styles'

/**
 * AP-49 8. — **Karte „Offene Punkte aus Besprechungen" im Projekt** (nur mit Projektverwaltung und
 * Projektbesprechung): je Serie die offenen Aufgaben des letzten freigegebenen Protokolls, überfällige
 * hervorgehoben; ein Klick öffnet das Protokoll. Liegt unter `components/`, weil das Projekt nicht in ein
 * anderes Feature greifen darf (AP-21-B); gelesen wird über den Endpunkt der Liste.
 */
export function ProjektOffenePunkte({ projectId }: { projectId: string }) {
  const { t } = useTranslation()
  const [zeilen, setZeilen] = useState<BesprechungOffenerPunkt[] | null>(null)
  const [gesamt, setGesamt] = useState(0)
  const [fehler, setFehler] = useState(false)

  useEffect(() => {
    let aktiv = true
    api
      .get<{ data: BesprechungOffenerPunkt[]; meta: { total: number } }>('/site-docs/meeting/open-items', { params: { projectId, limit: 20, sort: 'dueDate', dir: 'asc' } })
      .then((r) => {
        if (!aktiv) return
        setZeilen(r.data.data)
        setGesamt(r.data.meta.total)
      })
      .catch(() => aktiv && setFehler(true))
    return () => {
      aktiv = false
    }
  }, [projectId])

  return (
    <div className={formCard}>
      <h2 className="mb-2 flex items-center gap-2 text-sm font-medium">
        <ListChecks className="h-4 w-4" aria-hidden /> {t('siteDocs.meeting.openItems.card')}
      </h2>
      {fehler && <p className="text-sm text-destructive">{t('siteDocs.list.loadError')}</p>}
      {zeilen && zeilen.length === 0 && <p className="text-sm text-muted-foreground">{t('siteDocs.meeting.openItems.empty')}</p>}
      <ul className="divide-y divide-border">
        {zeilen?.map((z) => (
          <li key={z.id} className="flex items-center gap-3 py-1.5 text-sm">
            <span className="w-12 shrink-0 font-mono text-xs text-muted-foreground">{z.number}</span>
            <Link to={`/site-docs/${z.documentId}`} className="min-w-0 flex-1 truncate hover:underline" title={`${z.art} Nr. ${z.laufnummer}`}>
              {z.text}
            </Link>
            <span className="hidden shrink-0 text-xs text-muted-foreground sm:inline">{[z.assigneeName, z.assigneeCompany].filter(Boolean).join(', ')}</span>
            <span className={`shrink-0 text-xs ${z.overdue ? 'font-medium text-destructive' : 'text-muted-foreground'}`}>{z.dueDate ? fmtDate(z.dueDate) : z.dueText ?? ''}</span>
          </li>
        ))}
      </ul>
      {gesamt > (zeilen?.length ?? 0) && (
        <Link to={`/site-docs/meeting/open-items?projectId=${projectId}`} className="mt-2 inline-block text-sm text-primary hover:underline">
          {t('siteDocs.meeting.openItems.all')} ({gesamt})
        </Link>
      )}
    </div>
  )
}
